import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { battlesApi } from '../api';
import HrnbContainer, { HrnbRow, HrnbCol } from '../components/controls/HrnbContainer';
import HrnbButton from '../components/controls/HrnbButton';
import HrnbSelect from '../components/controls/HrnbSelect';
import HrnbInput from '../components/controls/HrnbInput';
import HrnbLoader from '../components/controls/HrnbLoader';
import HrnbSearch from '../components/controls/HrnbSearch';
import AudioPlayer from '../components/audio/AudioPlayer';
import '../css/pages/AudioPage.css';

const AudioPage = () => {
    const navigate = useNavigate();
    const [tracks, setTracks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterFormat, setFilterFormat] = useState('');
    const [sortBy, setSortBy] = useState('uploadedAt_DESC');
    const [searchQuery, setSearchQuery] = useState('');

    // Локальное состояние для плеера на странице Audio
    const [currentTrackId, setCurrentTrackId] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const FORMAT_OPTIONS = [
        { value: '', label: 'Все форматы' },
        { value: 'mp3', label: '🎵 MP3' },
        { value: 'wav', label: '🎵 WAV' },
        { value: 'ogg', label: '🎵 OGG' },
        { value: 'flac', label: '🎵 FLAC' },
        { value: 'aac', label: '🎵 AAC' },
        { value: 'm4a', label: '🎵 M4A' },
    ];

    const SORT_OPTIONS = [
        { value: 'uploadedAt_DESC', label: '🕐 Новые' },
        { value: 'uploadedAt_ASC', label: '📅 Старые' },
        { value: 'duration_DESC', label: '⏱️ Длинные' },
        { value: 'duration_ASC', label: '⏱️ Короткие' },
        { value: 'fileName_ASC', label: '🔤 По названию' },
    ];

    // Загрузка всех треков с пагинацией
    const fetchAllTracks = useCallback(async () => {
        try {
            setLoading(true);

            // Парсим sortBy и sortDir из состояния
            const [sortByField = 'uploadedAt', sortDir = 'DESC'] = sortBy.split('_');

            console.log('[AudioPage] Fetching tracks with params:', { page: 0, size: 100, sortBy: sortByField, sortDir, format: filterFormat || undefined });

            // Используем новый эндпоинт /api/tracks с пагинацией
            const tracksData = await battlesApi.getTracks({
                page: 0,
                size: 100,
                sortBy: sortByField,
                sortDir: sortDir,
                format: filterFormat || undefined
            });

            console.log('[AudioPage] Received tracks:', tracksData);
            const allTracks = tracksData?.tracks || [];
            console.log('[AudioPage] Processed tracks count:', allTracks.length);
            if (allTracks.length > 0) {
                console.log('[AudioPage] First track full:', JSON.stringify(allTracks[0], null, 2));
                console.log('[AudioPage] First track mediaUrl:', allTracks[0].mediaUrl);
            }
            setTracks(allTracks);
        } catch (err) {
            console.error('[AudioPage] Failed to fetch tracks:', err.message);
            // Fallback: старый метод загрузки через баттлы
            console.log('[AudioPage] Falling back to legacy method...');
            try {
                const battlesData = await battlesApi.list({ size: 100, status: 'FINISHED' });
                const battles = battlesData?.content || battlesData?.data || battlesData || [];

                const allTracks = [];

                for (const battle of battles) {
                    try {
                        const rounds = await battlesApi.getRounds(battle.id);

                        for (const round of rounds) {
                            try {
                                const roundWorks = await battlesApi.getRoundTracks(battle.id, round.id);

                                roundWorks.forEach(work => {
                                    // Новая структура: трек находится в work.track
                                    // Используем work.hasAudio для проверки наличия аудио
                                    const hasAudio = work.hasAudio !== undefined ? work.hasAudio : !!work.track;

                                    if (hasAudio && work.track) {
                                        allTracks.push({
                                            ...work.track,
                                            // Сохраняем ссылку на работу для доступа к статусу
                                            workId: work.id,
                                            workStatus: work.status,
                                            authorName: work.track.authorName || work.authorName,
                                            battleTitle: battle.title,
                                            battleId: battle.id,
                                            roundTitle: round.title,
                                            roundId: round.id,
                                            format: work.track.format || battle.format,
                                        });
                                    }
                                });
                            } catch (err) {
                                console.error(`[AudioPage] Failed to fetch tracks for round ${round.id}:`, err.message);
                            }
                        }
                    } catch (err) {
                        console.error(`[AudioPage] Failed to fetch rounds for battle ${battle.id}:`, err.message);
                    }
                }

                setTracks(allTracks);
            } catch (fallbackErr) {
                console.error('[AudioPage] Legacy method also failed:', fallbackErr.message);
            }
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFormatChange = (value) => {
        setFilterFormat(value);
    };

    const handleSortChange = (value) => {
        setSortBy(value);
    };

    const handleTrackSelect = (trackId) => {
        setCurrentTrackId(trackId);
        setIsPlaying(true);
    };

    const handleBattleClick = (battleId) => {
        navigate(`/battles/${battleId}`);
    };

    // Фильтрация и сортировка треков (клиентская для поиска)
    const getFilteredTracks = () => {
        let filtered = [...tracks];

        // Поиск по названию и баттлу
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(t =>
                (t.title && t.title.toLowerCase().includes(query)) ||
                (t.battleTitle && t.battleTitle.toLowerCase().includes(query)) ||
                (t.description && t.description.toLowerCase().includes(query)) ||
                (t.authorName && t.authorName.toLowerCase().includes(query))
            );
        }

        // Фильтруем треки без mediaUrl
        filtered = filtered.filter(t => t.mediaUrl);

        return filtered;
    };

    const filteredTracks = getFilteredTracks();

    // Загрузка треков при монтировании и изменении фильтров
    useEffect(() => {
        const loadTracksData = async () => {
            await fetchAllTracks();
        };
        loadTracksData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortBy, filterFormat]);

    if (loading) {
        return (
            <div className="audio-page">
                <HrnbContainer>
                    <h1 className="audio-page__title">🎵 Аудио платформа</h1>
                    <HrnbLoader size={48} text="Загрузка треков..." />
                </HrnbContainer>
            </div>
        );
    }

    return (
        <div className="audio-page">
            <h1 className="audio-page__title">🎵 Аудио платформа</h1>
            <p className="audio-page__subtitle">
                Все аудио-треки со всех баттлов площадки в одном месте
            </p>

            {/* Аудиоплеер на странице Audio */}
            <div className="audio-page__player-container">
                <AudioPlayer
                    standalone={false}
                    forceVisible={true}
                    forceExpanded={true}
                    tracks={filteredTracks}
                    currentTrackId={currentTrackId}
                    isPlaying={isPlaying}
                    onIsPlayingChange={setIsPlaying}
                />
            </div>

            <HrnbContainer>
                {/* Поиск и фильтры */}
                <HrnbRow className="audio-page__filters">
                    <HrnbCol xs={12} md={6}>
                        <HrnbInput
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="🔍 Поиск по названию, баттлу..."
                            className="audio-page__search"
                        />
                    </HrnbCol>
                    <HrnbCol xs={12} md={3}>
                        <HrnbSelect
                            value={filterFormat}
                            onChange={handleFormatChange}
                            options={FORMAT_OPTIONS}
                            placeholder="Фильтр по формату"
                            label="Формат"
                        />
                    </HrnbCol>
                    <HrnbCol xs={12} md={3}>
                        <HrnbSelect
                            value={sortBy}
                            onChange={handleSortChange}
                            options={SORT_OPTIONS}
                            placeholder="Сортировка"
                            label="Сортировка"
                        />
                    </HrnbCol>
                </HrnbRow>

                {/* Статистика */}
                <HrnbRow className="audio-page__stats">
                    <HrnbCol xs={12}>
                        <div className="audio-page__stats-bar">
                            <span>📀 Треков: {filteredTracks.length}</span>
                            {filterFormat && (
                                <span>🏷️ Фильтр: {FORMAT_OPTIONS.find(o => o.value === filterFormat)?.label}</span>
                            )}
                        </div>
                    </HrnbCol>
                </HrnbRow>

                {/* Список треков */}
                {filteredTracks.length === 0 ? (
                    <HrnbRow>
                        <HrnbCol xs={12}>
                            <div className="audio-page__empty">
                                <div className="audio-page__empty-icon">🎵</div>
                                <h3>Треки не найдены</h3>
                                <p>Попробуйте изменить параметры фильтрации</p>
                            </div>
                        </HrnbCol>
                    </HrnbRow>
                ) : (
                    <HrnbRow>
                        <HrnbCol xs={12}>
                            <div className="audio-page__tracks-grid">
                                {filteredTracks.map((track, index) => (
                                    <TrackCard
                                        key={track.id}
                                        track={track}
                                        index={index + 1}
                                        isSelected={track.id === currentTrackId}
                                        isPlaying={isPlaying}
                                        onSelect={() => handleTrackSelect(track.id)}
                                        onBattleClick={() => handleBattleClick(track.battleId)}
                                    />
                                ))}
                            </div>
                        </HrnbCol>
                    </HrnbRow>
                )}
            </HrnbContainer>
        </div>
    );
};

// Карточка трека
function TrackCard({ track, index, isSelected, isPlaying, onSelect, onBattleClick }) {
    // Формат трека (mp3, wav, ogg и т.д.)
    const formatUpper = (track.format || 'mp3').toUpperCase();

    const formatDuration = (duration) => {
        if (!duration || isNaN(duration)) return '--:--';
        const mins = Math.floor(duration / 60);
        const secs = Math.floor(duration % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Проверяем, играет ли текущий трек
    const isCurrentTrackPlaying = isSelected && isPlaying;

    return (
        <div className={`audio-track-card ${isSelected ? 'active' : ''}`} onClick={onSelect}>
            <div className="audio-track-card__header">
                <span className="audio-track-card__index">{index}</span>
                <span className="audio-track-card__format">🎵</span>
                {isCurrentTrackPlaying && <span className="audio-track-card__playing">🔊</span>}
            </div>
            
            <div className="audio-track-card__body">
                <h3 className="audio-track-card__title">{track.title || 'Без названия'}</h3>
                {track.authorName && (
                    <p className="audio-track-card__artist">🎤 {track.authorName}</p>
                )}
                {track.description && (
                    <p className="audio-track-card__description">
                        {track.description.length > 100
                            ? `${track.description.substring(0, 100)}...`
                            : track.description}
                    </p>
                )}
                <div className="audio-track-card__meta">
                    <span className="audio-track-card__battle" onClick={(e) => { e.stopPropagation(); onBattleClick(); }}>
                        📀 {track.battleTitle || track.roundTitle || 'Баттл'}
                    </span>
                    {track.roundTitle && track.battleTitle && (
                        <span className="audio-track-card__round">
                            🎯 {track.roundTitle}
                        </span>
                    )}
                    {track.duration && (
                        <span className="audio-track-card__duration">
                            ⏱️ {formatDuration(track.duration)}
                        </span>
                    )}
                    {track.format && (
                        <span className="audio-track-card__format-badge">
                            {formatUpper}
                        </span>
                    )}
                </div>
            </div>
            
            <div className="audio-track-card__actions">
                <HrnbButton variant="primary" size="sm" onClick={(e) => { e.stopPropagation(); onSelect(); }}>
                    {isCurrentTrackPlaying ? '⏸' : '▶'}
                </HrnbButton>
            </div>
        </div>
    );
}

export default AudioPage;
